import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDownIcon, CheckIcon, BookOpenIcon } from "@heroicons/react/16/solid"
import { ModuleGroup } from "../hooks/useCourseLessonMap"

export type LessonFilter =
  | { type: "all" }
  | { type: "module"; id: string }
  | { type: "lesson"; id: string }

type Props = {
  modules: ModuleGroup[]
  value: LessonFilter
  onChange: (f: LessonFilter) => void
}

const filterLabel = (filter: LessonFilter, modules: ModuleGroup[]) => {
  if (filter.type === "all") return "Все сообщения"
  if (filter.type === "module") {
    const m = modules.find((mm) => mm.moduleId === filter.id)
    return m ? m.title : "Модуль"
  }
  for (const m of modules) {
    const l = m.lessons.find((ll) => ll.id === filter.id)
    if (l) return l.title
  }
  return "Урок"
}

export const LessonFilterBar = ({ modules, value, onChange }: Props) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const updatePos = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (r) setPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 260) })
    }
    updatePos()

    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }

    window.addEventListener("scroll", updatePos, true)
    window.addEventListener("resize", updatePos)
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("scroll", updatePos, true)
      window.removeEventListener("resize", updatePos)
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  if (modules.length === 0) return null

  const isActive = value.type !== "all"
  const select = (f: LessonFilter) => { onChange(f); setOpen(false) }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-lg text-xs font-medium border transition-colors max-w-[220px] ${
          isActive ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
        }`}
      >
        <BookOpenIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate">{filterLabel(value, modules)}</span>
        <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
          className="z-[70] bg-white border border-gray-200 rounded-xl shadow-lg shadow-black/5 py-1.5 max-h-96 overflow-auto"
        >
          <button
            type="button"
            onClick={() => select({ type: "all" })}
            className={`w-full flex items-center gap-2 px-3.5 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
              value.type === "all" ? "text-blue-700 font-semibold" : "text-gray-700"
            }`}
          >
            <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
              {value.type === "all" && <CheckIcon className="w-4 h-4 text-blue-600" />}
            </span>
            Все сообщения
          </button>

          {modules.map((m) => (
            <div key={m.moduleId} className="mt-0.5">
              <button
                type="button"
                onClick={() => select({ type: "module", id: m.moduleId })}
                className={`w-full flex items-center gap-1.5 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-left transition-colors hover:bg-gray-50 ${
                  value.type === "module" && value.id === m.moduleId ? "text-blue-600" : "text-gray-400"
                }`}
              >
                {value.type === "module" && value.id === m.moduleId && <CheckIcon className="w-3 h-3" />}
                <span className="truncate">{m.title}</span>
              </button>
              {m.lessons.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => select({ type: "lesson", id: l.id })}
                  className={`w-full flex items-center gap-2 pl-7 pr-3.5 py-1.5 text-sm text-left transition-colors hover:bg-gray-50 ${
                    value.type === "lesson" && value.id === l.id ? "text-blue-700 font-semibold" : "text-gray-600"
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                    {value.type === "lesson" && value.id === l.id && <CheckIcon className="w-3.5 h-3.5 text-blue-600" />}
                  </span>
                  <span className="truncate">{l.title}</span>
                </button>
              ))}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}
